import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { FileText, Lock, ShieldAlert, Ban, Users } from 'lucide-react';

const auth = (t) => ({ headers: { Authorization: `Bearer ${t}` } });

const AUD_STYLE = {
  client: 'border-[#8FC93A] bg-[#8FC93A]/5',
  operations: 'border-amber-300 bg-amber-50',
  worker: 'border-sky-300 bg-sky-50',
  sergio: 'border-red-300 bg-red-50',
};

// Distinct audience views (client, operations, assigned-worker, Sergio-only) generated
// from ONE inspection. All delivery stays disabled in Preview.
const SynovusReview = ({ inspectionId, inspectionNumber, onClose, preview = false, defaultLang = null }) => {
  const { token, user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [matrix, setMatrix] = useState(null);
  const [lang, setLang] = useState({});

  useEffect(() => {
    axios.get(`${API}/operations/synovus/inspections/${inspectionId}/delivery-review`, auth(token)).then((r) => {
      setData(r.data);
      // Seed each audience's output language from the form the user was working in
      // (defaultLang) when that audience supports it; otherwise keep its own default.
      const l = {}; (r.data.packages || []).forEach((p) => {
        const supported = p.languages || ['en'];
        l[p.audience] = (defaultLang && supported.includes(defaultLang)) ? defaultLang : p.default_language;
      }); setLang(l);
    }).catch(() => {});
    axios.get(`${API}/operations/synovus/inspections/${inspectionId}/audience-matrix`, auth(token)).then((r) => setMatrix(r.data)).catch(() => {});
  }, [inspectionId, token, defaultLang]);

  const openReport = (kind, l) => window.open(`${API}/operations/synovus/inspections/${inspectionId}/report/${kind}.pdf?lang=${l}&auth=${token}`, '_blank');

  const es = defaultLang === 'es';
  const T = {
    titlePrev: es ? 'Vista previa del informe (Borrador)' : 'Report Preview (Draft)',
    titleRev: es ? 'Revisión de informe y entrega' : 'Report & Delivery Review',
    descPrev: es ? 'Vista previa de solo lectura del informe de cada destinatario con las respuestas ingresadas hasta ahora. Abrir una vista previa nunca envía ni crea un Plan de Acción.'
                 : 'Read-only preview of each audience report using the answers entered so far. Opening a preview never submits, sends, or creates a Plan of Action.',
    descRev: es ? 'Cuatro versiones por destinatario de la misma inspección.' : 'Four audience versions from the same inspection.',
    noticePrev: es ? 'Solo vista previa — una vista previa nunca envía, manda un mensaje ni crea una acción correctiva. Los borradores incompletos se marcan como “BORRADOR — INCOMPLETO”.'
                   : 'Preview only — a preview never submits, sends a message, or creates a corrective action. Incomplete drafts are marked “DRAFT — INCOMPLETE”.',
    noticeRev: es ? 'Entrega deshabilitada en Preview — crear un borrador o vista previa nunca envía un mensaje.' : 'Delivery disabled in Preview — creating a draft or preview never sends a message.',
    recipients: es ? 'Destinatarios:' : 'Recipients:',
    noneResolved: es ? 'ninguno resuelto' : 'none resolved',
    open: es ? 'Abrir' : 'Open',
    matrixTitle: es ? 'Divulgación de campos por destinatario (aplicada en el servidor)' : 'Field disclosure by audience (server-enforced)',
    field: es ? 'Campo' : 'Field',
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="syn-review-dialog">
        <DialogHeader>
          <DialogTitle>{preview ? T.titlePrev : T.titleRev} — {inspectionNumber}</DialogTitle>
          <DialogDescription>{preview ? T.descPrev : T.descRev} {data?.delivery_status}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] space-y-4 overflow-y-auto">
          <div className="flex items-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600" data-testid="syn-review-delivery-status">
            <Ban size={14} /> {preview ? T.noticePrev : T.noticeRev}
          </div>

          {(data?.packages || []).map((p) => (
            <div key={p.audience} className={`rounded-xl border p-3 ${AUD_STYLE[p.audience] || 'border-slate-200'}`} data-testid={`syn-review-pkg-${p.audience}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-semibold text-[#0E2A47]">
                  {p.audience === 'sergio' ? <ShieldAlert size={15} className="text-red-600" /> : p.audience === 'client' ? <Users size={15} className="text-[#4d7c0f]" /> : <Lock size={15} className="text-slate-500" />}
                  {p.title}
                </div>
                <div className="flex items-center gap-1">
                  {(p.languages || ['en']).map((l) => (
                    <button key={l} onClick={() => setLang((s) => ({ ...s, [p.audience]: l }))} data-testid={`syn-review-lang-${p.audience}-${l}`}
                      className={`rounded px-2 py-0.5 text-[11px] font-bold ${lang[p.audience] === l ? 'bg-[#0E2A47] text-white' : 'bg-white text-slate-500 border border-slate-300'}`}>{l.toUpperCase()}</button>
                  ))}
                </div>
              </div>
              <p className="mt-1 text-[11px] text-slate-500">{p.rule}</p>
              <div className="mt-2 text-xs">
                <span className="font-semibold text-slate-600">{T.recipients}</span>{' '}
                {(p.recipients || []).length === 0 ? <span className="text-slate-400">{T.noneResolved}</span> :
                  p.recipients.map((r, i) => <span key={i} className="mr-1 rounded bg-white/70 px-1.5 py-0.5" data-testid={`syn-review-rcpt-${p.audience}-${i}`}>{r.name} · {r.role}{r.language ? ` · ${r.language.toUpperCase()}` : ''}</span>)}
              </div>
              {(p.blocked || []).length > 0 && (
                <div className="mt-1 text-[11px] text-red-600">{p.blocked.map((b, i) => <div key={i}>⚠ {b}</div>)}</div>
              )}
              <div className="mt-2 flex flex-wrap gap-2">
                <button onClick={() => openReport(p.report, lang[p.audience] || 'en')} data-testid={`syn-review-open-${p.audience}`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#0E2A47] px-3 py-1.5 text-xs font-semibold text-white">
                  <FileText size={13} /> {T.open} {(lang[p.audience] || 'en').toUpperCase()} PDF
                </button>
              </div>
            </div>
          ))}

          <p className="rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500">{data?.additional_services_note}</p>

          {matrix && (
            <div className="rounded-xl border border-slate-200" data-testid="syn-review-matrix">
              <div className="border-b border-slate-100 px-3 py-2 text-xs font-bold text-[#0E2A47]">{T.matrixTitle}</div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-[11px]">
                  <thead className="bg-slate-50 text-left text-slate-500"><tr><th className="px-3 py-1.5">{T.field}</th><th className="px-2 py-1.5">Client</th><th className="px-2 py-1.5">Ops</th><th className="px-2 py-1.5">Worker</th><th className="px-2 py-1.5">Sergio</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {matrix.matrix.map((row, i) => (
                      <tr key={i}><td className="px-3 py-1.5 text-slate-600">{row.field}</td>
                        <td className="px-2 py-1.5">{row.client}</td><td className="px-2 py-1.5">{row.operations}</td>
                        <td className="px-2 py-1.5">{row.worker}</td><td className="px-2 py-1.5 font-semibold text-red-700">{row.sergio}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="px-3 py-2 text-[10px] text-slate-400">{matrix.note}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SynovusReview;
